/**
 * Cashback percentage (`client.bonusLevel`) resolution and input validation.
 *
 * Kept in a standalone, dependency-free module so it is trivially unit-testable (no jotai /
 * AtomsProvider import chain) and shareable between the cart maths and the client editor.
 *
 * WHY THIS EXISTS. The cart computed cashback as:
 *
 *     total * (client.bonusLevel || 3) / 100
 *
 * and `bonusLevel` is edited through a FREE-TEXT `Input`, so it holds whatever was typed. That one
 * expression carried two money defects, both observed live on 2026-07-18:
 *
 *   • JUNK BECOMES NaN. Client "Сергей Селеверстов" (Пушка-гонка57) held the string "5%". A non-empty
 *     string is truthy, so `|| 3` did not fire and `total * "5%" / 100` evaluated to NaN — the label
 *     rendered "5%" while the accrued amount was NaN.
 *   • ZERO BECOMES THE DEFAULT. `0 || 3` is 3, so a client deliberately set to 0% cashback was given
 *     3% instead. Three clients in Supabase sit at 0.
 *
 * `||` cannot express this correctly: it cannot distinguish "unset" from "set to zero". Hence an
 * explicit resolver.
 */

/** Applied when a client has no usable level. Matches the historical `|| 3` fallback and
 *  `CreateClientView`'s `defaultBonusLevel || 3`. */
export const DEFAULT_BONUS_LEVEL = 3

/**
 * Resolve a client's cashback percentage to a finite, non-negative number.
 *
 * Guarantees — relied on by the cart totals — that the result is ALWAYS a finite number, so no input
 * can turn a customer's total into NaN.
 *
 * A trailing '%' is stripped before parsing: the UI renders the '%' itself, so a user typing it means
 * the number (this is exactly how "5%" arose). Anything still unparseable, negative, or non-finite
 * falls back to {@link DEFAULT_BONUS_LEVEL} rather than propagating into the arithmetic. A high level
 * is NOT capped — a live client sits at 500 as a deliberate goodwill gesture, and silently clamping a
 * real business decision would be its own bug.
 */
export const resolveBonusLevel = (client) => {
  const raw = client?.bonusLevel

  if (raw === null || raw === undefined) return DEFAULT_BONUS_LEVEL
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_BONUS_LEVEL
  }
  if (typeof raw !== 'string') return DEFAULT_BONUS_LEVEL

  // A COMMA is a decimal separator on ru/kk devices — `keyboardType='numeric'` renders the platform
  // keypad, whose decimal key follows the device locale. Number() does not understand it, so it is
  // normalised to a dot here rather than rejected: rejecting it meant "2,5" became "25", i.e. 25%
  // cashback instead of 2.5% (ATL-730).
  const trimmed = raw.trim().replace(/\s*%\s*$/, '').replace(',', '.').trim()
  if (trimmed === '') return DEFAULT_BONUS_LEVEL

  // Number('') is 0 and Number(' ') is 0, both already excluded above. Reject anything that is not a
  // plain non-negative decimal so '5%5', '1.2.3', '1,2,3' and '1e999' cannot slip through — note the
  // replace above swaps only the FIRST comma, so a second one survives to fail this test.
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return DEFAULT_BONUS_LEVEL

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_BONUS_LEVEL
}

/**
 * Whether a keystroke-level value is acceptable in the bonusLevel box.
 *
 * Permits the empty string — clearing the field is meaningful ("no specific level, use the default"),
 * and the backend DTO explicitly accepts `''` for exactly that reason — and permits a trailing '.'
 * so a decimal can be typed. Rejects '%', letters and negatives at the source, so junk never reaches
 * Firestore in the first place; {@link resolveBonusLevel} then handles the values already stored.
 */
export const isValidBonusLevelInput = (value) => {
  if (value === '' || value === null || value === undefined) return true
  if (typeof value !== 'string') return false
  // Either separator is accepted — a ru/kk numeric keypad emits a comma, and rejecting the keystroke
  // silently turned "2,5" into "25" (ATL-730). Exactly one separator, so "1,2,3" and "1.2,3" fail.
  return /^\d*([.,]\d*)?$/.test(value) && value !== '.' && value !== ','
}
